/* tslint:disable:max-classes-per-file no-bitwise */
export class ShelfPack {
    /**
     * Create a new ShelfPack bin allocator.
     *
     * @param  w Width of the sprite
     * @param  h Height of the sprite
     * @example
     * var sprite = new ShelfPack(64, 64, { autoResize: false });
     */
    constructor(w = 64, h = 64) {
        this.shelves = [];
        this.freeBins = [];
        this.stats = {};
        this.bins = {};
        this.maxId = 0;
        this.w = w;
        this.h = h;
    }
    /**
     * Batch pack multiple bins into the sprite.
     *
     * @param   bins       Array of requested bins
     * @returns Array of allocated Bins - each Bin is an object with `id`, `x`, `y`, `w`, `h` properties
     * @example
     * var bins = [
     *     { id: 1, w: 12, h: 12 },
     *     { id: 2, w: 12, h: 16 },
     *     { id: 3, w: 12, h: 24 }
     * ];
     * var results = sprite.pack(bins);
     */
    pack(bins) {
        bins = [...bins];
        const results = [];
        for (const item of bins) {
            const allocation = this.packOne(item.w, item.h, item.id);
            if (!allocation) {
                continue;
            }
            results.push(allocation);
        }
        return results;
    }
    /**
     * Pack a single bin into the sprite.
     *
     * Each bin will have a unique identitifer.
     * If no identifier is supplied in the `id` parameter, one will be created.
     * Note: The supplied `id` is used as an object index, so numeric values are fastest!
     *
     * Bins are automatically refcounted (i.e. a newly packed Bin will have a refcount of 1).
     * When a bin is no longer needed, use the `ShelfPack.unref` function to mark it
     *   as unused.  When a Bin's refcount decrements to 0, the Bin will be marked
     *   as free and its space may be reused by the packing code.
     *
     * @param    w      Width of the bin to allocate
     * @param    h      Height of the bin to allocate
     * @param    id     Unique identifier for this bin, (if unsupplied, assume it's a new bin and create an id)
     * @returns  Bin object with `id`, `x`, `y`, `w`, `h` properties, or `null` if allocation failed
     * @example
     * var results = sprite.packOne(12, 16, 'a');
     */
    packOne(w, h, id) {
        const best = { freebin: -1, shelf: -1, waste: Infinity };
        // if id was supplied, attempt a lookup..
        if (typeof id === "string" || typeof id === "number") {
            const bin = this.getBin(id);
            if (bin) {
                // we packed this bin already
                this.ref(bin);
                return bin;
            }
            if (typeof id === "number") {
                this.maxId = Math.max(id, this.maxId);
            }
        }
        else {
            id = ++this.maxId;
        }
        // First try to reuse a free bin..
        for (let i = 0; i < this.freeBins.length; i++) {
            const bin = this.freeBins[i];
            // exactly the right height and width, use it..
            if (h === bin.maxh && w === bin.maxw) {
                return this.allocFreebin(i, w, h, id);
            }
            // not enough height or width, skip it..
            if (h > bin.maxh || w > bin.maxw) {
                continue;
            }
            // extra height or width, minimize wasted area..
            if (h <= bin.maxh && w <= bin.maxw) {
                const waste = bin.maxw * bin.maxh - w * h;
                if (waste < best.waste) {
                    best.waste = waste;
                    best.freebin = i;
                }
            }
        }
        // Next find the best shelf..
        let y = 0;
        for (let i = 0; i < this.shelves.length; i++) {
            const shelf = this.shelves[i];
            y += shelf.h;
            // not enough width on this shelf, skip it..
            if (w > shelf.free) {
                continue;
            }
            // exactly the right height, pack it..
            if (h === shelf.h) {
                return this.allocShelf(i, w, h, id);
            }
            // not enough height, skip it..
            if (h > shelf.h) {
                continue;
            }
            // extra height, minimize wasted area..
            if (h < shelf.h) {
                const waste = (shelf.h - h) * w;
                if (waste < best.waste) {
                    best.freebin = -1;
                    best.waste = waste;
                    best.shelf = i;
                }
            }
        }
        if (best.freebin !== -1) {
            return this.allocFreebin(best.freebin, w, h, id);
        }
        if (best.shelf !== -1) {
            return this.allocShelf(best.shelf, w, h, id);
        }
        // No free bins or shelves.. add shelf..
        if (h <= this.h - y && w <= this.w) {
            const shelf = new Shelf(y, this.w, h);
            return this.allocShelf(this.shelves.push(shelf) - 1, w, h, id);
        }
        return null;
    }
    /**
     * Called by packOne() to allocate a bin by reusing an existing freebin
     *
     * @private
     * @param    index  Index into the `this.freeBins` array
     * @param    w      Width of the bin to allocate
     * @param    h      Height of the bin to allocate
     * @param    id     Unique identifier for this bin
     * @returns  Bin object with `id`, `x`, `y`, `w`, `h` properties
     * @example
     * var bin = sprite.allocFreebin(0, 12, 16, 'a');
     */
    allocFreebin(index, w, h, id) {
        const bin = this.freeBins.splice(index, 1)[0];
        bin.id = id;
        bin.w = w;
        bin.h = h;
        bin.refcount = 0;
        this.bins[id] = bin;
        this.ref(bin);
        return bin;
    }
    /**
     * Called by `packOne() to allocate bin on an existing shelf
     *
     * @private
     * @param    index  Index into the `this.shelves` array
     * @param    w      Width of the bin to allocate
     * @param    h      Height of the bin to allocate
     * @param    id     Unique identifier for this bin
     * @returns  Bin object with `id`, `x`, `y`, `w`, `h` properties
     * @example
     * var results = sprite.allocShelf(0, 12, 16, 'a');
     */
    allocShelf(index, w, h, id) {
        const shelf = this.shelves[index];
        const bin = shelf.alloc(w, h, id);
        if (bin !== null) {
            this.bins[id] = bin;
            this.ref(bin);
        }
        return bin;
    }
    /**
     * Return a packed bin given its id, or undefined if the id is not found
     *
     * @param    id  Unique identifier for this bin,
     * @returns  The requested bin, or undefined if not yet packed
     * @example
     * var b = sprite.getBin('a');
     */
    getBin(id) {
        return this.bins[id];
    }
    /**
     * Increment the ref count of a bin and update statistics.
     *
     * @param    bin  Bin instance
     * @returns  New refcount of the bin
     * @example
     * var bin = sprite.getBin('a');
     * sprite.ref(bin);
     */
    ref(bin) {
        if (++bin.refcount === 1) {
            // a new Bin.. record height in stats historgram..
            const h = bin.h;
            this.stats[h] = (this.stats[h] | 0) + 1;
        }
        return bin.refcount;
    }
    /**
     * Decrement the ref count of a bin and update statistics.
     * The bin will be automatically marked as free space once the refcount reaches 0.
     *
     * @param    bin  Bin instance
     * @returns  New refcount of the bin
     * @example
     * var bin = sprite.getBin('a');
     * sprite.unref(bin);
     */
    unref(bin) {
        if (bin.refcount === 0) {
            return 0;
        }
        if (--bin.refcount === 0) {
            this.stats[bin.h]--;
            delete this.bins[bin.id];
            this.freeBins.push(bin);
        }
        return bin.refcount;
    }
    /**
     * Clear the sprite.  Resets everything and resets statistics.
     *
     * @example
     * sprite.clear();
     */
    clear() {
        this.shelves = [];
        this.freeBins = [];
        this.stats = {};
        this.bins = {};
        this.maxId = 0;
    }
}
class Shelf {
    /**
     * Create a new Shelf.
     *
     * @param  {number}  y   Top coordinate of the new shelf
     * @param  {number}  w   Width of the new shelf
     * @param  {number}  h   Height of the new shelf
     * @example
     * var shelf = new Shelf(64, 512, 24);
     */
    constructor(y, w, h) {
        this.x = 0;
        this.y = y;
        this.w = w;
        this.free = w;
        this.h = h;
    }
    /**
     * Allocate a single bin into the shelf.
     *
     * @param   w   Width of the bin to allocate
     * @param   h   Height of the bin to allocate
     * @param   id  Unique id of the bin to allocate
     * @returns Bin object with `id`, `x`, `y`, `w`, `h` properties, or `null` if allocation failed
     * @example
     * shelf.alloc(12, 16, 'a');
     */
    alloc(w, h, id) {
        if (w > this.free || h > this.h) {
            return null;
        }
        const x = this.x;
        this.x += w;
        this.free -= w;
        return createBin(id, x, this.y, w, h, w, this.h);
    }
    /**
     * Resize the shelf.
     *
     * @param   w  Requested new width of the shelf
     * @returns true
     * @example
     * shelf.resize(512);
     */
    resize(w) {
        this.free += w - this.w;
        this.w = w;
        return true;
    }
}
/**
 * Create a new Bin object.
 *
 * @class  Bin
 * @param  id   Unique id of the bin
 * @param  x    Left coordinate of the bin
 * @param  y    Top coordinate of the bin
 * @param  w    Width of the bin
 * @param  h    Height of the bin
 * @param  maxw Max width of the bin (defaults to `w` if not provided)
 * @param  maxh Max height of the bin (defaults to `h` if not provided)
 * @example
 * var bin = new Bin('a', 0, 0, 12, 16);
 */
function createBin(id, x, y, w, h, maxw, maxh) {
    return {
        id,
        x,
        y,
        w,
        h,
        maxw: maxw || w,
        maxh: maxh || h,
        refcount: 0,
    };
}
